import { useEffect, useMemo, useState } from "react";
import { getHeadersForCategory } from "../../utils/headers";
import type {
  DiffTableProps,
  VehicleData,
  VehicleTableData
} from "@dealer-center/ddc-pages-inventory-archiver-interface";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import { DataTable } from "./DataTable";

const DiffTable = (props: DiffTableProps) => {
  const [columns, setColumns] =
    useState<TypeSafeTableColumns<VehicleTableData>>();
  const diffRows = useMemo(() => {
    // 1) Key both datasets by VIN so the same vehicle lines up
    const getVinKey = (v: VehicleData): string =>
      v.vin?.trim().toUpperCase() || "";

    const preMap = new Map<string, VehicleData>();
    props.data.preData.forEach(item => {
      const key = getVinKey(item);
      if (key) preMap.set(key, item);
    });
    const postMap = new Map<string, VehicleData>();
    props.data.postData.forEach(item => {
      const key = getVinKey(item);
      if (key) postMap.set(key, item);
    });

    const sharedVins = props.data.preData
      .map(getVinKey)
      .filter(k => k && postMap.has(k));

    const ignoredColumns = new Set(["vin"]);

    const diffs: VehicleTableData[] = [];
    let displayIndex = 0;

    sharedVins.forEach(vin => {
      const preItem = preMap.get(vin) as unknown as Record<string, unknown>;
      const postItem = postMap.get(vin) as unknown as Record<string, unknown>;

      const row: VehicleTableData = {
        key: `diff-${vin}`,
        className: "diff-row",
        title: `Vehicle ${displayIndex + 1}`
      };

      let hasAnyDiff = false;

      props.checkedCategories.forEach(category => {
        getHeadersForCategory(category).forEach(headerKey => {
          const preValue = preItem?.[headerKey] ?? "None";
          const postValue = postItem?.[headerKey] ?? "None";

          const preStr = preValue?.toString();
          const postStr = postValue?.toString();

          row[`pre_${headerKey}`] = preStr;
          row[`post_${headerKey}`] = postStr;
          row[headerKey] = [`${preStr}`, `${postStr}`];

          if (!ignoredColumns.has(headerKey) && preStr !== postStr) {
            hasAnyDiff = true;
          }
        });
      });

      if (hasAnyDiff) {
        displayIndex += 1;
        diffs.push(row);
      }
    });

    return diffs;
  }, [props.data.preData, props.data.postData, props.checkedCategories]);
  useEffect(() => {
    const headers: TypeSafeTableColumns<VehicleTableData> =
      props.checkedCategories.flatMap(category =>
        getHeadersForCategory(category).map((key: string) => ({
          title: key,
          key,
          dataIndex: key as keyof VehicleTableData,
          className: `column-${key?.toLowerCase()}`,
          render: (_: unknown, row: VehicleTableData) => {
            const preValue = row[`pre_${key}`];
            const postValue = row[`post_${key}`];
            if (preValue == postValue || !preValue) {
              return <div className="no-diff">{postValue}</div>;
            }
            return (
              <div className="diff">
                <div style={{ color: "red" }}>{preValue}</div>
                <center>↓</center>
                <div style={{ color: "green" }}>{postValue}</div>
              </div>
            );
          }
        }))
      );
    setColumns(headers);
  }, [props.checkedCategories]);

  return (
    <div>
      <DataTable
        checkedCategories={props.checkedCategories}
        columns={columns || []}
        emptyText="No differences found"
        headerData={props.data.preData}
        rows={diffRows}
        onToggleHeaders={props.onToggleHeaders}
      />
    </div>
  );
};

export default DiffTable;
